import { useState, type ReactNode } from 'react';

import { Input } from '@sshop/ui';

import {
  digitsOnly,
  registrationErrors,
  type RegistrationField,
  type RegistrationValues,
} from './registrationValidation';

const successKeys = {
  name: 'nameValid',
  surname: 'surnameValid',
  idNumber: 'personalIdValid',
  phone: 'phoneValid',
  email: 'emailValid',
  password: 'passwordValid',
  passwordRepeat: 'passwordsMatch',
} as const;

type Props = {
  values: RegistrationValues;
  errors: Partial<Record<RegistrationField, string>>;
  onChange: (field: RegistrationField, value: string) => void;
  onBlur: (field: RegistrationField) => void;
  t: (key: string) => string;
  busy: boolean;
  compact?: boolean;
  idPrefix: string;
};

export function RegistrationFields({
  values,
  errors,
  onChange,
  onBlur,
  t,
  busy,
  compact,
  idPrefix,
}: Props) {
  const [copied, setCopied] = useState(false);
  const validation = registrationErrors(values, t);
  const copyPassword = async () => {
    if (!values.password) return;
    try {
      await navigator.clipboard.writeText(values.password);
      setCopied(true);
      return;
    } catch {
      // Clipboard permissions vary in embedded browsers; use selection as a fallback.
    }

    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const temporary = document.createElement('textarea');
    temporary.value = values.password;
    temporary.setAttribute('readonly', '');
    temporary.style.position = 'fixed';
    temporary.style.left = '-9999px';
    document.body.appendChild(temporary);
    temporary.select();
    const copiedWithSelection = document.execCommand('copy');
    temporary.remove();
    active?.focus();
    setCopied(copiedWithSelection);
  };
  const field = (
    key: Exclude<RegistrationField, 'agree'>,
    label: string,
    hint: string,
    options: {
      type?: string;
      autoComplete?: string;
      inputMode?: 'numeric';
      maxLength?: number;
      suffix?: ReactNode;
    } = {},
  ) => {
    const valid =
      Boolean(values[key]) &&
      !validation[key] &&
      !errors[key] &&
      (key !== 'passwordRepeat' || !validation.password);
    return (
      <Input
        id={`${idPrefix}-${key}`}
        label={`${label} *`}
        hint={t(hint)}
        error={errors[key]}
        success={valid ? t(successKeys[key]) : undefined}
        value={values[key]}
        onChange={(event) => {
          if (key === 'password') setCopied(false);
          onChange(
            key,
            options.inputMode === 'numeric'
              ? digitsOnly(event.target.value, options.maxLength ?? 20)
              : event.target.value,
          );
        }}
        onBlur={() => onBlur(key)}
        disabled={busy}
        required
        {...options}
      />
    );
  };

  return (
    <div className={`grid grid-cols-1 gap-x-4 ${compact ? 'gap-y-3' : 'gap-y-4'} sm:grid-cols-2`}>
      {field('name', t('firstName'), 'nameRequirement', {
        autoComplete: 'given-name',
        maxLength: 255,
      })}
      {field('surname', t('lastName'), 'surnameRequirement', {
        autoComplete: 'family-name',
        maxLength: 255,
      })}
      <div className="sm:col-span-2">
        {field('idNumber', t('personalId'), 'personalIdInvalid', {
          inputMode: 'numeric',
          maxLength: 11,
          autoComplete: 'off',
        })}
      </div>
      <div className="sm:col-span-2">
        {field('phone', t('phone'), 'phoneInvalid', {
          type: 'tel',
          inputMode: 'numeric',
          maxLength: 9,
          autoComplete: 'tel-national',
        })}
      </div>
      <div className="sm:col-span-2">
        {field('email', t('email'), 'emailHint', { type: 'email', autoComplete: 'email' })}
      </div>
      {field('password', t('password'), 'passwordRequirement', {
        type: 'password',
        autoComplete: 'new-password',
        suffix: (
          <button
            type="button"
            className="text-accent text-xs font-semibold hover:underline disabled:opacity-50"
            onClick={() => void copyPassword()}
            disabled={!values.password || busy}
          >
            {t(copied ? 'passwordCopied' : 'copyPassword')}
          </button>
        ),
      })}
      {field('passwordRepeat', t('passwordRepeat'), 'passwordRepeatHint', {
        type: 'password',
        autoComplete: 'new-password',
      })}
    </div>
  );
}
